import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { useLoading } from '../../../hooks/useLoading';
import { Link, NavLink } from 'react-router-dom';
import { Button, ProgressLoader } from '../Elements';
import ConfirmationModal from '../ConfirmationModal';
import MaterailTable from '../Elements/MaterialTable';
import { FaEllipsisV, FaUsers } from 'react-icons/fa';
import { ListItemIcon, Menu, MenuItem } from '@mui/material';
import { getNavigationUrl } from '../../../common/helpers/navigationHelpers';
import { Edit, HowToReg, PersonRemove } from '@mui/icons-material';
import { getStatusInfo } from '../TableStatusView';
import getUserTypeName from '../TableUserTypeView';
import { UserListType } from '../../../common/types/UserTypes';
import { tableEmptyMessages } from '../../../constants/messages/tableEmptyMessages';
import { toast } from 'react-toastify';
import { successMessages } from '../../../constants/messages/successMessages';
import { refreshUserTable } from '../../../common/helpers/refreshTables/refreshUserTable';
import { ThunkDispatch } from 'redux-thunk';
import commonNetwork from '../../../common/api/commonNetwork';
import { ApiResponseType } from '../../../common/api/apiHelper';
import { muiMenuStyles } from '../../../constants/muiMenuStyles';
import { hide } from '@popperjs/core';

interface UserTableProps {
    formFilterRef: React.Ref<HTMLDivElement> | undefined;
    getHeadCells: (values: any) => void;
    tableSelectedRows: any;
}

const UserTable: React.FC<UserTableProps> = ({ formFilterRef, getHeadCells, tableSelectedRows }) => {
    const classes = muiMenuStyles();

    const dispatch = useDispatch<ThunkDispatch<any, any, any>>();

    // useRef's
    const confirmModalRef = useRef<any>();

    // useState's
    const [anchorEl, setAnchorEl] = useState(null);
    const [dropdownMenuId, setDropdownMenuId] = useState("");
    const [dropdownAllValues, setDropdownAllValues] = useState<UserListType>();
    const [operationsLoading, setOperationsLoading] = useState(false);

    useEffect(() => {
        getHeadCells(headCells);
        refreshUserTable(dispatch);
    }, [])

    const isopenMenu = Boolean(anchorEl);
    const {
        userList,
        isFetching,
        isloadingIntegration,
        isFetchComplete
    } = useSelector((state: any) => state.userList);

    const loading = useLoading(isFetching);
    const handleClickMenu = (event: any, row: any) => {
        setDropdownAllValues(row);
        setDropdownMenuId(row.id);
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const LinearColor = () => {
        if (operationsLoading || isloadingIntegration) {
            return <ProgressLoader />;
        }
        return null;
    };

    const handleStatus = async (userId: any, status: boolean | any) => {
        const paramaters = {
            userId: userId,
            status: status ? false : true
        }

        let result: ApiResponseType;

        result = await commonNetwork.post("/user/update-status", paramaters);

        if (result.isSuccess) {
            toast.success(status ? successMessages.statusPassive : successMessages.statusActive);
            refreshUserTable(dispatch);
            setAnchorEl(null);
        } else {
            toast.error(result.errorMessage);
        }
    }

    const DropdownMenu = (row: any) => {
        const status = dropdownAllValues?.status
        return (
            <Menu
                id="user-operations"
                anchorEl={anchorEl}
                open={isopenMenu}
                onClose={handleClose}
                classes={{
                    paper: classes.paper,
                }}
            >
                <MenuItem component={Link} to={getNavigationUrl(`/kullanici-guncelle/` + dropdownAllValues?.userId)}>
                    <ListItemIcon>
                        <Edit fontSize="small" color='primary' />
                    </ListItemIcon>
                    Düzenle
                </MenuItem>
                <MenuItem onClick={() => handleStatus(dropdownAllValues?.userId, status)}
                >
                    <ListItemIcon>
                        {status === true ? (
                            <PersonRemove color="error" />
                        ) : (
                            <HowToReg color="success" />
                        )}

                    </ListItemIcon>
                    {status === true ? "Kullanıcıyı Pasif Et" : "Kullanıcıyı Aktif Et"}
                </MenuItem>
                <MenuItem component={Link} to={getNavigationUrl(`/kullanici-musteri-yetkilendir/` + dropdownAllValues?.userId)}>
                    <ListItemIcon>
                        <FaUsers fontSize="medium" color='green' />
                    </ListItemIcon>
                    Müşteri Eşleştir
                </MenuItem>
            </Menu>
        )
    };

    const headCells = [
        {
            id: 'userId',
            numeric: false,
            disablePadding: true,
            label: 'ID',
            isprimary: true,
            className: "min-w-[80px]",
            colWidth: 10, // For excel
            hide: true,
            exceptToExcel: true
        },
        {
            id: 'operations',
            numeric: false,
            label: 'İşlemler',
            className: "max-w-[65px]",
            exceptToExcel: true,
            isRender: true,
            disableSortIcon: true,
            renderRow: (row: any) => (
                <>
                    {row.userType === 1 ? null :
                        <div className="flex flex-row justify-around items-center ">
                            <a
                                id="report-operations-a"
                                aria-controls={isopenMenu ? 'report-operations' : undefined}
                                aria-haspopup="true"
                                aria-expanded={isopenMenu ? 'true' : undefined}
                                className="cursor-pointer"
                                onClick={(event) => handleClickMenu(event, row)}
                            >
                                <FaEllipsisV
                                    className="block sm:block ml-1 h-3 w-3 font-light"
                                    aria-hidden="true"
                                />
                            </a>
                            <DropdownMenu />
                        </div>}
                </>
            )
        },
        {
            id: 'userCode',
            numeric: false,
            label: 'Kullanıcı Kodu',
            className: "min-w-[170px]",
            forcealign: "center",
            maxLength: 30,
            colWidth: 20,
        },
        {
            id: 'userName',
            numeric: false,
            label: 'Kullanıcı Adı',
            className: "min-w-[170px]",
            maxLength: 30,
            colWidth: 20,
        },
        {
            id: 'email',
            numeric: false,
            label: 'Kullanıcı E-Posta Adresi',
            className: "min-w-[200px]",
            maxLength: 35,
            colWidth: 30, // For excel
        },
        {
            id: 'phone',
            numeric: false,
            label: 'Telefon',
            colWidth: 20,
            className: "min-w-[110px]",
            maxLength: 30,
        },
        {
            id: 'userType',
            numeric: true,
            disablePadding: false,
            label: 'Tipi',
            className: "min-w-[150px]",
            isRender: true,
            colWidth: 30,
            renderRow: (row: any) => (
                <>
                    {getUserTypeName(row.userType)}
                </>
            )
        },
        {
            id: 'status',
            numeric: true,
            disablePadding: false,
            className: "min-w-[130px]",
            isRender: true,
            label: 'Durum',
            colWidth: 20,
            renderRow: (row: any) => (
                <>
                    {getStatusInfo(row.status)}
                </>
            )
        }
    ];

    return (
        <>
            {LinearColor()}
            <ConfirmationModal ref={confirmModalRef} />
            <MaterailTable
                ref={tableSelectedRows}
                headerCells={headCells}
                tableData={userList}
                isRowSelect
                isLoading={loading}
                emptyMessage={isFetchComplete ? tableEmptyMessages.isFilteredDataNotFound : tableEmptyMessages.emptyString}
            />
        </>
    );
};

export default UserTable;